<script setup>
import { computed, ref } from 'vue';
import { useI18n } from 'vue-i18n';
import { useStore } from 'dashboard/composables/store';

import Dialog from 'dashboard/components-next/dialog/Dialog.vue';
import Button from 'dashboard/components-next/button/Button.vue';
import Input from 'dashboard/components-next/input/Input.vue';
import TextArea from 'dashboard/components-next/textarea/TextArea.vue';
import ComboBox from 'dashboard/components-next/combobox/ComboBox.vue';
import ConversationSelector from './ConversationSelector.vue';

const props = defineProps({
  card: {
    type: Object,
    default: null,
  },
  isSaving: {
    type: Boolean,
    default: false,
  },
});

const emit = defineEmits(['submit', 'close']);

const { t } = useI18n();
const store = useStore();

const dialogRef = ref(null);
const cardTitle = ref('');
const cardDescription = ref('');
const cardAssigneeId = ref('');
const cardConversationId = ref('');
const cardConversationLabel = ref('');

const isEditing = computed(() => !!props.card);
const dialogTitle = computed(() =>
  isEditing.value
    ? t('KANBAN.CARDS.EDIT_TITLE')
    : t('KANBAN.CARDS.CREATE_TITLE')
);

const availableAgents = computed(
  () => store.getters['agents/getVerifiedAgents'] || []
);

const agentOptions = computed(() =>
  availableAgents.value.map(agent => ({ label: agent.name, value: agent.id }))
);

const open = () => {
  cardTitle.value = props.card?.title || '';
  cardDescription.value = props.card?.description || '';
  cardAssigneeId.value = props.card?.assignee_id || '';
  cardConversationId.value = props.card?.conversation_id || '';
  cardConversationLabel.value = cardConversationId.value
    ? `#${cardConversationId.value}`
    : '';
  if (!availableAgents.value.length) {
    store.dispatch('agents/get');
  }
  dialogRef.value?.open();
};

const handleSubmit = () => {
  if (!cardTitle.value.trim()) {
    return;
  }
  emit('submit', {
    title: cardTitle.value.trim(),
    description: cardDescription.value,
    assignee_id: cardAssigneeId.value || '',
    conversation_id: cardConversationId.value || '',
  });
};

defineExpose({ open, close: () => dialogRef.value?.close() });
</script>

<template>
  <Dialog
    ref="dialogRef"
    :title="dialogTitle"
    :show-cancel-button="false"
    :show-confirm-button="false"
    @close="emit('close')"
  >
    <div class="flex flex-col gap-4">
      <Input
        v-model="cardTitle"
        :label="t('KANBAN.CARDS.TITLE_LABEL')"
        :placeholder="t('KANBAN.CARDS.TITLE_PLACEHOLDER')"
        autofocus
      />
      <TextArea
        v-model="cardDescription"
        :label="t('KANBAN.CARDS.DESCRIPTION_LABEL')"
        :placeholder="t('KANBAN.CARDS.DESCRIPTION_PLACEHOLDER')"
        :max-length="500"
        resize
      />
      <div class="flex flex-col gap-1">
        <ComboBox
          :model-value="cardAssigneeId"
          :options="agentOptions"
          :placeholder="t('KANBAN.CARDS.ASSIGNEE_PLACEHOLDER')"
          :search-placeholder="t('KANBAN.CARDS.ASSIGNEE_SEARCH_PLACEHOLDER')"
          :empty-state="t('KANBAN.CARDS.ASSIGNEE_EMPTY')"
          @update:model-value="cardAssigneeId = $event"
        />
        <Button
          v-if="cardAssigneeId"
          class="self-end !px-2 !h-6"
          variant="ghost"
          size="sm"
          :label="t('KANBAN.CARDS.UNASSIGN')"
          icon="i-lucide-user-x"
          @click="cardAssigneeId = ''"
        />
      </div>
      <div class="flex flex-col gap-1">
        <ConversationSelector
          v-model="cardConversationId"
          :display-label="cardConversationLabel"
        />
        <Button
          v-if="cardConversationId"
          class="self-end !px-2 !h-6"
          variant="ghost"
          size="sm"
          :label="t('KANBAN.CARDS.UNLINK_CONVERSATION')"
          icon="i-lucide-link-2-off"
          @click="cardConversationId = ''"
        />
      </div>
    </div>
    <template #footer>
      <div class="flex items-center justify-between w-full gap-3">
        <Button
          variant="faded"
          class="w-full"
          :label="t('KANBAN.CARDS.CANCEL')"
          @click="dialogRef?.close()"
        />
        <Button
          color="blue"
          class="w-full"
          :label="t('KANBAN.CARDS.SAVE')"
          :is-loading="isSaving"
          :disabled="isSaving || !cardTitle.trim()"
          type="submit"
          @click="handleSubmit"
        />
      </div>
    </template>
  </Dialog>
</template>
